import { subscribeReferralConsent } from '@/services/consentInvalidation.js';
import React from "react";
import { useNavigate } from "react-router-dom";

import BasicDialog from "@/components/ui/BasicDialog.jsx";
import Card from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  loadReferralStandingAuthorisations,
  revokeReferralStandingAuthorisation,
} from "@/services/referralPackageService.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";

function formatDate(value, locale) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat(locale || undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsed);
}

export default function ReferralSharing() {
  const nav = useNavigate();
  const { t, languageCode } = useLanguage();
  const [rows, setRows] = React.useState([]);
  const [state, setState] = React.useState("loading");
  const [error, setError] = React.useState("");
  const [revoking, setRevoking] = React.useState(null);
  const [selected, setSelected] = React.useState(null);

  const requestSequence = React.useRef(0);
  const load = React.useCallback(async () => {
    const sequence = ++requestSequence.current;
    setState("loading");
    setError("");
    try {
      const next = await loadReferralStandingAuthorisations();
      if (sequence !== requestSequence.current) return;
      setRows(next);
      setState("ready");
    } catch (caught) {
      if (sequence !== requestSequence.current) return;
      setError(String(caught?.message || t("referrals.sharing.loadError", "Unable to load referral sharing permissions.")));
      setState("error");
    }
  }, [t]);

  React.useEffect(() => {
    void load();
    const unsubscribe = subscribeReferralConsent(load);
    return () => { unsubscribe(); requestSequence.current++; };
  }, [load]);

  const activeRows = rows.filter((row) => !row?.revoked_at);
  const selectedReferrer = resolvePatientFacingDoctorName(selected?.referring_doctor);
  const selectedRecipient = resolvePatientFacingDoctorName(selected?.receiving_doctor);

  async function confirmRevoke() {
    if (!selected?.id) return;
    setRevoking(selected.id);
    setError("");
    try {
      await revokeReferralStandingAuthorisation(selected.id);
      setRows((current) => current.map((row) => (
        row.id === selected.id
          ? { ...row, revoked_at: new Date().toISOString() }
          : row
      )));
      setSelected(null);
    } catch (caught) {
      setError(String(caught?.message || t("referrals.sharing.revokeError", "Unable to revoke this referral sharing permission.")));
    } finally {
      setRevoking(null);
    }
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter
        close
        onBack={() => nav("/settings/patient/preferences")}
        title={t("referrals.sharing.title", "Referral sharing")}
      />

      <div className="mt-6 space-y-4">
        <p className="text-body-medium text-on-surface-var">
          {t(
            "referrals.sharing.intro",
            "Manage the clinician pairs you have allowed to share future referral packages. This does not grant access to your health record.",
          )}
        </p>

        {state === "loading" ? (
          <div className="text-body-medium text-on-surface-var">
            {t("common.loading", "Loading…")}
          </div>
        ) : null}

        {state === "ready" && activeRows.length === 0 ? (
          <Card className="px-4 py-5 text-body-medium text-on-surface-var">
            {t("referrals.sharing.empty", "No recurring referral permissions are active.")}
          </Card>
        ) : null}

        {activeRows.map((row) => {
          const referrerName = resolvePatientFacingDoctorName(row.referring_doctor);
          const recipientName = resolvePatientFacingDoctorName(row.receiving_doctor);
          return (
            <Card key={row.id} className="space-y-4 px-4 py-4">
              <div className="text-title-small text-on-surface">
                {referrerName} → {recipientName}
              </div>
              <div className="text-body-medium text-on-surface">
                {t("referrals.sharing.alwaysAllowed", "Future referrals: Always allowed")}
              </div>
              <div className="text-body-small text-on-surface-var">
                {t("referrals.sharing.granted", "Granted: {date}", {
                  date: formatDate(row.granted_at, languageCode),
                })}
              </div>
              <Button
                variant="tonal"
                className="min-h-11 rounded-xl text-label-large text-[rgb(var(--schemes-critical))]"
                style={{ background: "var(--state-critical-08)" }}
                onClick={() => setSelected(row)}
              >
                {t("referrals.sharing.revoke", "Revoke")}
              </Button>
            </Card>
          );
        })}

        {error ? <div className="text-body-small text-[rgb(var(--afrimed-error))]">{error}</div> : null}
      </div>

      <BasicDialog
        open={Boolean(selected)}
        title={t("referrals.sharing.revokeTitle", "Revoke recurring referral permission?")}
        body={t(
          "referrals.sharing.revokeBody",
          "Future referrals from {referrerName} to {recipientName} will ask you again. Previously authorised referral packages remain unchanged.",
          { referrerName: selectedReferrer, recipientName: selectedRecipient },
        )}
        cancelText={t("common.cancel", "Cancel")}
        okText={t("referrals.sharing.revoke", "Revoke")}
        disableOk={Boolean(revoking)}
        disableBackdropClose={Boolean(revoking)}
        onCancel={() => setSelected(null)}
        onOk={confirmRevoke}
      />
    </SettingsScreenShell>
  );
}
